/* Temple Mount Soil — Cart Drawer
 * Vanilla web component. Wraps Shopify's AJAX Cart API.
 *
 * Public events:
 *   - 'cart:open'        → opens drawer
 *   - 'cart:close'       → closes drawer
 *   - 'cart:refresh'     → re-fetches /cart.js and re-renders
 *
 * Public DOM API (via document.querySelector('tms-cart-drawer')):
 *   - .open(), .close(), .toggle()
 *   - .refresh()
 *
 * Form interception:
 *   Any <form action="/cart/add"> on the page is intercepted, items are added
 *   via fetch to /cart/add.js, then the drawer opens with the fresh cart.
 */

(function () {
  if (customElements.get('tms-cart-drawer')) return;

  const ROUTES = (window.routes && window.routes.cart_url) ? window.routes : {
    cart_url: '/cart',
    cart_add_url: '/cart/add',
    cart_change_url: '/cart/change',
    cart_update_url: '/cart/update',
  };

  // Money in the market's own format (shop.money_format follows the selected market, for
  // example "{{amount}} NIS"). The theme never defined Shopify.formatMoney, so every dynamic
  // update used to fall back to "$" in every currency (fixed Oct 10 2026).
  const formatWith = (cents, format) => {
    const ph = /\{\{\s*(\w+)\s*\}\}/;
    const f = (typeof format === 'string' && ph.test(format)) ? format : '${{amount}}';
    const num = (n, precision, thousands, decimal) => {
      if (n == null || isNaN(n)) n = 0;
      const parts = (n / 100).toFixed(precision).split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    };
    let value;
    switch (f.match(ph)[1]) {
      case 'amount_no_decimals': value = num(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = num(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = num(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = num(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = num(cents, 0, ' ', ','); break;
      case 'amount_with_space_separator': value = num(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = num(cents, 2, ' ', '.'); break;
      default: value = num(cents, 2, ',', '.');
    }
    return f.replace(ph, () => value);
  };
  // The amounts in a cart are in that cart's currency. On market pages other scripts call root
  // cart URLs and can leave the cart in USD, so the page's format is used only when the cart's
  // currency is the page's currency; otherwise the amount is written in the cart's own currency.
  let cartCurrency = null; // set by _render from cart.currency
  const fmtMoneyIn = (cents, currency) => {
    const s = window.cartDrawerSettings || {};
    const page = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || null;
    if (currency && page && currency !== page) {
      try {
        return new Intl.NumberFormat('en', { style: 'currency', currency: currency }).format((cents || 0) / 100);
      } catch (_) {}
    }
    return formatWith(cents, s.moneyFormat);
  };
  const fmtMoney = (cents) => fmtMoneyIn(cents, cartCurrency);
  window.tmsFormatMoney = fmtMoney;
  window.tmsFormatMoneyIn = fmtMoneyIn;

  // Compact escape — line item titles can contain special chars
  const escapeHtml = (s) =>
    String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this._onKey = this._onKey.bind(this);
      this._onDocClick = this._onDocClick.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
    }

    connectedCallback() {
      // Bind UI events scoped to the drawer
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-cd-action]');
        if (!t) return;
        e.preventDefault();
        const action = t.dataset.cdAction;
        const key = t.dataset.cdKey;

        if (action === 'close') return this.close();
        if (action === 'inc') return this._changeQtyByKey(key, +1);
        if (action === 'dec') return this._changeQtyByKey(key, -1);
        if (action === 'remove') return this._setQtyByKey(key, 0);
        if (action === 'checkout') return (window.location.href = '/checkout');
      });

      // Global wiring (capture phase for click + submit so we beat the theme's
      // own handlers that may stopPropagation in bubble phase).
      document.addEventListener('keydown', this._onKey);
      document.addEventListener('click', this._onDocClick, true);
      document.addEventListener('submit', this._onSubmit, true);

      // Custom events
      document.addEventListener('cart:open', () => this.open());
      document.addEventListener('cart:close', () => this.close());
      document.addEventListener('cart:refresh', () => this.refresh());

      // Theme editor: auto-open when this section is selected so merchants can preview it
      if (window.Shopify && window.Shopify.designMode) {
        const isOurs = (target) => target && (
          target === this ||
          (target.querySelector && target.querySelector('tms-cart-drawer'))
        );
        document.addEventListener('shopify:section:select',  (e) => isOurs(e.target) && this.open());
        document.addEventListener('shopify:section:load',    (e) => isOurs(e.target) && this.open());
        document.addEventListener('shopify:section:deselect',(e) => isOurs(e.target) && this.close());
        document.addEventListener('shopify:section:unload',  (e) => isOurs(e.target) && this.close());
      }

      // A page restored by the Back button keeps the drawer exactly as it was left; read the cart again.
      window.addEventListener('pageshow', (e) => { if (e.persisted) this.refresh(); });
      // Escape belongs to a window opened on top of the cart (Giftnote's gift message) while it is
      // open. Noted in the capture phase, before that window's own handler closes it.
      window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this._modalAtKey = this._giftnoteOpen();
      }, true);

      this.setAttribute('data-ready', '');
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKey);
      document.removeEventListener('click', this._onDocClick, true);
      document.removeEventListener('submit', this._onSubmit, true);
    }

    _onKey(e) {
      if (e.key !== 'Escape' || !this.hasAttribute('open')) return;
      const modal = this._modalAtKey || this._giftnoteOpen();
      this._modalAtKey = false;
      if (modal) return; // the gift message window closes itself; the cart stays
      this.close();
    }

    // Is the Giftnote app's own window open? It lives in <giftnote-core>'s shadow root, outside the drawer.
    _giftnoteOpen() {
      try {
        const core = document.querySelector('giftnote-core');
        const m = core && core.shadowRoot && core.shadowRoot.querySelector('#giftnote-modal');
        return !!(m && !m.classList.contains('hidden') && getComputedStyle(m).display !== 'none');
      } catch (_) { return false; }
    }

    // Opened by the visitor (cart icon, a link to /cart): show the cart as the server has it now,
    // not as this page last drew it (another tab, or a page the browser restored).
    _openFresh() {
      this.open();
      this.refresh();
    }

    _onDocClick(e) {
      // Highest priority: any element wired up via aria-controls="cart-drawer"
      // (the existing TMS header cart icon uses this). Capture-phase listener
      // means we beat the theme's own toggle handler.
      const ariaTrigger = e.target.closest('[aria-controls="cart-drawer"]');
      if (ariaTrigger) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this._openFresh();
        return;
      }
      // Explicit opt-in trigger
      const opener = e.target.closest('[data-cd-open]');
      if (opener) {
        e.preventDefault();
        this._openFresh();
        return;
      }
      // Auto-trigger: any anchor pointing to /cart. Opt out per-link with data-cd-skip.
      const link = e.target.closest('a[href]');
      if (link && !link.hasAttribute('data-cd-skip')) {
        const href = link.getAttribute('href') || '';
        const cartUrl = ROUTES.cart_url || '/cart';
        const isCartLink =
          href === cartUrl || href === cartUrl + '/' ||
          href.startsWith(cartUrl + '?') || href.startsWith(cartUrl + '#') ||
          /(^|\/)cart(\/|\?|#|$)/.test(href);
        if (isCartLink) {
          e.preventDefault();
          this._openFresh();
          return;
        }
      }
      // Close on overlay click
      if (e.target === this.querySelector('.cd-overlay')) this.close();
    }

    async _onSubmit(e) {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = (form.getAttribute('action') || '').split('?')[0];
      if (!/\/cart\/add$/.test(action)) return;
      // Skip forms that explicitly opt out
      if (form.hasAttribute('data-cd-skip')) return;

      // "Buy it now" is the product form's second submit button (name="return_to"). The theme's own
      // product form answers that button as well, so the product was added TWICE and checkout
      // opened with double the quantity (found Oct 12 2026). The drawer now takes it alone:
      // one add, then straight to checkout, and the theme's handler never sees the submit.
      const buyNow = !!(e.submitter && e.submitter.name === 'return_to');
      e.preventDefault();
      if (buyNow) e.stopImmediatePropagation();
      this._clearError();
      // Open immediately with a busy state — the content reconciles when
      // the add + refresh complete. Perceived latency drops to ~0 even
      // though the network cost is unchanged.
      if (!buyNow) this.open();
      const drawerPane = this.querySelector('.cd-drawer');
      if (drawerPane) drawerPane.setAttribute('aria-busy', 'true');
      const submitBtn = (buyNow && e.submitter) || form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.setAttribute('aria-busy', 'true');

      try {
        const fd = new FormData(form);
        // TMS price A/B test: never add while that script is writing to the cart (two cart writes
        // at the same moment can cancel one another).
        if (window.tmsPABIdle) await window.tmsPABIdle();
        const res = await fetch(`${ROUTES.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: fd,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Could not add to cart');
        }
        await this.refresh();
        if (buyNow) {
          // The refresh above tells the cart's listeners about the new line (the price test writes
          // its code then); wait for that write, then go. The code travels in the link as well,
          // the same way the drawer's own checkout button carries it.
          if (window.tmsPABIdle) await window.tmsPABIdle();
          let url = '/checkout';
          try {
            const st = window.tmsPAB && window.tmsPAB.state && window.tmsPAB.state();
            if (st && st.test && !st.failed && st.want) url += '?discount=' + encodeURIComponent(st.want);
          } catch (_) {}
          window.location.href = url;
          return;
        }
        this.open();
      } catch (err) {
        console.warn('[cart-drawer] add failed:', err);
        // A network failure arrives as a TypeError whose text ("Failed to fetch") means nothing to a shopper.
        const msg = (err && err.name === 'TypeError')
          ? 'We could not reach the store. Please check your connection and try again.'
          : err.message;
        // Tell the page FIRST that the add failed: code waiting on the add (the "This is a gift"
        // box, which opens the gift message window on the next cart update) must stand down
        // before the refresh below announces a cart update.
        // Two listener contracts exist in this theme. assets/theme.js BuyButtons binds
        // to the FORM and reads detail.error; other code listens on document for
        // detail.message. Emit both so neither side is silently ignored.
        try {
          form.dispatchEvent(new CustomEvent('cart:error', {
            bubbles: true, detail: { error: msg, message: msg },
          }));
        } catch (_) {}
        document.dispatchEvent(new CustomEvent('cart:error', {
          detail: { error: msg, message: msg },
        }));
        // The drawer was opened optimistically before this request resolved, so it is
        // currently showing whatever state it held at page load. Reconcile it against
        // the server before saying anything, otherwise a failed add leaves the shopper
        // looking at a stale — and often empty — cart with no explanation.
        if (buyNow) this.open();
        await this.refresh();
        this._showError(msg);
      } finally {
        if (submitBtn) submitBtn.removeAttribute('aria-busy');
        const dp = this.querySelector('.cd-drawer');
        if (dp) dp.removeAttribute('aria-busy');
      }
    }

    // --- error surface -------------------------------------------------
    // Lives outside .cd-body because _render() replaces that element's innerHTML.

    _errorEl() {
      let el = this.querySelector('.cd-error');
      if (el) return el;
      const drawer = this.querySelector('.cd-drawer');
      const body = this.querySelector('.cd-body');
      if (!drawer || !body) return null;

      if (!document.getElementById('cd-error-style')) {
        const style = document.createElement('style');
        style.id = 'cd-error-style';
        style.textContent = [
          '.cd-error{',
          'display:none;margin:0;padding:12px 16px;',
          'background:rgba(179,38,30,0.08);',
          'border-top:1px solid rgba(179,38,30,0.35);',
          'border-bottom:1px solid rgba(179,38,30,0.35);',
          'color:#8a1c15;font-size:14px;line-height:1.5;}',
          '.cd-error[data-shown]{display:block;}',
        ].join('');
        document.head.appendChild(style);
      }

      el = document.createElement('p');
      el.className = 'cd-error';
      el.setAttribute('role', 'alert');
      el.setAttribute('aria-live', 'assertive');
      // Above the scrolling list (v2 wraps the list in .cd-scrollwrap), so it cannot scroll out of view.
      const anchor = body.closest('.cd-scrollwrap') || body;
      anchor.parentNode.insertBefore(el, anchor);
      return el;
    }

    _showError(message) {
      const el = this._errorEl();
      if (!el) return;
      el.textContent = message || 'We could not add that to your cart. Please try again.';
      el.setAttribute('data-shown', '');
    }

    _clearError() {
      const el = this.querySelector('.cd-error');
      if (!el) return;
      el.removeAttribute('data-shown');
      el.textContent = '';
    }

    async _setQtyByKey(key, qty, retried) {
      const row = this.querySelector(`.cd-item[data-cd-key="${CSS.escape(key)}"]`);
      if (row) row.setAttribute('aria-busy', 'true');
      try {
        if (window.tmsPABIdle) await window.tmsPABIdle();
        const res = await fetch(`${ROUTES.cart_change_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty }),
        });
        if (!res.ok) {
          // TMS price A/B test (Oct 12 2026): a discount code landing on the cart gives every line
          // a new key, so a key drawn a moment earlier can be refused. Find the line again by its
          // variant and try once more.
          if (!retried) {
            const fresh = await (await fetch(`${ROUTES.cart_url}.js`, { headers: { 'Accept': 'application/json' } })).json();
            const vid = String(key).split(':')[0];
            const hits = (fresh.items || []).filter((i) => String(i.key).split(':')[0] === vid);
            if (hits.length === 1 && hits[0].key !== key) return this._setQtyByKey(hits[0].key, qty, true);
          }
          throw new Error('change failed');
        }
        // /cart/change.js already returns the FULL cart — render it directly
        // instead of paying for a second /cart.js round-trip via refresh().
        let cart = await res.json();
        // TMS price A/B test (Oct 12 2026): the test arm sees its price on the line itself (snippets/tms-price-ab.liquid).
        if (window.tmsPABView) cart = window.tmsPABView(cart);
        this._clearError();
        this._render(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } catch (err) {
        console.warn('[cart-drawer] change failed:', err);
        if (row) row.removeAttribute('aria-busy');
        this.refresh();
        // A refused or failed change used to be silent: the row simply did nothing.
        this._showError('We could not update your cart. Please try again.');
      }
    }

    async _changeQtyByKey(key, delta) {
      const row = this.querySelector(`.cd-item[data-cd-key="${CSS.escape(key)}"]`);
      if (!row) return;
      const valEl = row.querySelector('.cd-qty__value');
      const current = parseInt(valEl?.textContent || '0', 10);
      const next = Math.max(0, current + delta);
      return this._setQtyByKey(key, next);
    }

    async refresh() {
      try {
        const res = await fetch(`${ROUTES.cart_url}.js`, {
          headers: { 'Accept': 'application/json' },
        });
        let cart = await res.json();
        // TMS price A/B test (Oct 12 2026): the test arm sees its price on the line itself (snippets/tms-price-ab.liquid).
        if (window.tmsPABView) cart = window.tmsPABView(cart);
        this._render(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } catch (err) {
        console.warn('[cart-drawer] refresh failed:', err);
      }
    }

    open() {
      if (!this.hasAttribute('open')) {
        const ae = document.activeElement;
        this._opener = (ae && ae !== document.body && !this.contains(ae)) ? ae : null;
      }
      this.setAttribute('open', '');
      document.documentElement.classList.add('cd-locked');
      document.body.classList.add('cd-locked');
      // Move focus to close button for a11y
      const closeBtn = this.querySelector('[data-cd-action="close"]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }

    close() {
      const wasOpen = this.hasAttribute('open');
      this.removeAttribute('open');
      document.documentElement.classList.remove('cd-locked');
      document.body.classList.remove('cd-locked');
      this._clearError();
      // Hand keyboard focus back to whatever opened the cart (it used to stay inside the closed drawer).
      const back = this._opener;
      this._opener = null;
      if (wasOpen && back && document.contains(back)) {
        try { back.focus({ preventScroll: true }); } catch (_) {}
      }
    }

    toggle() { this.hasAttribute('open') ? this.close() : this.open(); }

    _render(cart) {
      const drawer = this.querySelector('.cd-drawer');
      if (!drawer) return;

      const itemCount = cart.item_count || 0;
      const countEl = this.querySelector('[data-cd-count]');
      if (countEl) countEl.textContent = `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`;
      cartCurrency = cart.currency || cartCurrency;
      // Cart v2 (test arm): unit count in the title, and an empty flag for the layout.
      const count2El = this.querySelector('[data-cd2-count]');
      if (count2El) count2El.textContent = itemCount;
      this.toggleAttribute('data-cd-empty', itemCount === 0);

      const bodyEl = this.querySelector('.cd-body');
      if (!bodyEl) return;

      // The list is rebuilt from scratch below, which used to throw keyboard focus to <body> after
      // every plus, minus or Remove. Remember the focused control and find it again afterwards.
      let refocus = null;
      const focused = document.activeElement;
      if (focused && bodyEl.contains(focused) && focused.dataset && focused.dataset.cdAction) {
        refocus = { action: focused.dataset.cdAction, key: focused.dataset.cdKey || '' };
      }

      if (itemCount === 0) {
        bodyEl.innerHTML = this._emptyHtml();
        const footer = this.querySelector('.cd-footer');
        if (footer) footer.hidden = true;
        if (refocus) {
          const closeBtn = this.querySelector('[data-cd-action="close"]');
          if (closeBtn) { try { closeBtn.focus({ preventScroll: true }); } catch (_) {} }
        }
        return;
      }

      // Render items + promo
      const promoHtml = this._promoHtml(cart);
      const itemsHtml = cart.items.map((it) => this._itemHtml(it)).join('');
      bodyEl.innerHTML = `${promoHtml}<div class="cd-items">${itemsHtml}</div>`;
      if (refocus) {
        const sameAction = Array.from(bodyEl.querySelectorAll(`[data-cd-action="${refocus.action}"]`));
        const variant = String(refocus.key).split(':')[0];
        const target =
          sameAction.find((b) => b.dataset.cdKey === refocus.key) ||                       // same line
          sameAction.find((b) => String(b.dataset.cdKey || '').split(':')[0] === variant) || // same product, new line key
          bodyEl.querySelector('[data-cd-action]') ||
          this.querySelector('[data-cd-action="close"]');
        if (target) { try { target.focus({ preventScroll: true }); } catch (_) {} }
      }

      // Footer totals
      const footer = this.querySelector('.cd-footer');
      if (footer) footer.hidden = false;
      const strikeEl = this.querySelector('[data-cd-original]');
      const totalEl = this.querySelector('[data-cd-subtotal]');
      const ctaTotalEl = this.querySelector('[data-cd-cta-subtotal]');

      // Original total = sum of original_line_price across items. cart.items_subtotal_price
      // is post line-level discounts and can equal total_price when items are individually
      // marked free (e.g. BOGO at 100%), so it's not a reliable "before discount" total.
      const original = (cart.items || []).reduce(
        (sum, it) => sum + (it.original_line_price ?? it.line_price ?? 0), 0
      );
      const subtotal = cart.total_price;
      const hasDiscount = original > subtotal;

      if (totalEl) totalEl.textContent = fmtMoney(subtotal);
      if (ctaTotalEl) ctaTotalEl.textContent = fmtMoney(subtotal);
      if (strikeEl) {
        if (hasDiscount && original != null && original > subtotal) {
          strikeEl.hidden = false;
          strikeEl.textContent = fmtMoney(original);
        } else {
          strikeEl.hidden = true;
        }
      }
      // Cart v2 (test arm): one Subtotal row, the amount after product offers.
      const sub2El = this.querySelector('[data-cd2-subtotal]');
      const orig2El = this.querySelector('[data-cd2-original]');
      // Not cart.total_price: after a shipping estimate, /cart/change.js adds that shipping
      // rate into total_price. The item lines minus any order-level offer are always right.
      const cartLevel = (cart.cart_level_discount_applications || []).reduce(
        (sum, a) => sum + (a.total_allocated_amount || 0), 0
      );
      const net = (typeof cart.items_subtotal_price === 'number')
        ? Math.max(0, cart.items_subtotal_price - cartLevel)
        : subtotal;
      if (sub2El) sub2El.textContent = fmtMoney(net);
      if (orig2El) {
        const struck = original > net;
        orig2El.hidden = !struck;
        if (struck) orig2El.textContent = fmtMoney(original);
      }
    }

    _emptyHtml() {
      const settings = window.cartDrawerSettings || {};
      const continueUrl = settings.continueShoppingUrl || '/collections/all';
      return `
        <div class="cd-empty">
          <div class="cd-empty__icon" aria-hidden="true">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/>
              <path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
          </div>
          <div class="cd-empty__title">${escapeHtml(settings.emptyTitle || 'Your cart awaits')}</div>
          <div class="cd-empty__sub">${escapeHtml(settings.emptySubtitle || 'Begin your journey as a custodian of a two-thousand-year-old story.')}</div>
          <a class="cd-empty__cta" href="${escapeHtml(continueUrl)}">${escapeHtml(settings.emptyCta || 'Explore the Collection')}</a>
        </div>
      `;
    }

    _promoHtml(cart) {
      const s = window.cartDrawerSettings || {};
      if (!s.promoEnabled) return '';
      const unlocked = !!(cart && cart.items && cart.items.some(it =>
        (it.original_line_price ?? 0) > (it.final_line_price ?? it.line_price ?? 0)
      ));
      const title = unlocked
        ? (s.promoTitleUnlocked || 'Discount Applied')
        : (s.promoTitle || 'Up to 20% Off Selected Keepsakes');
      const sub = unlocked
        ? (s.promoSubUnlocked || 'Your savings have been applied at checkout.')
        : (s.promoSub || 'Discounts apply automatically at checkout.');
      const icon = unlocked
        ? `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
             <path d="M20 6 9 17l-5-5"/>
           </svg>`
        : `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
             <path d="M11 3H4v7l11 11 7-7L11 3Z"/><circle cx="8" cy="7" r="1.2"/>
           </svg>`;
      return `
        <div class="cd-promo${unlocked ? ' cd-promo--unlocked' : ''}" data-cd-promo>
          <div class="cd-promo__icon" aria-hidden="true">${icon}</div>
          <div style="min-width:0;">
            <div class="cd-promo__title">${escapeHtml(title)}</div>
            <div class="cd-promo__sub">${escapeHtml(sub)}</div>
          </div>
        </div>
      `;
    }

    _itemHtml(it) {
      const key = escapeHtml(it.key);
      const img = it.featured_image && it.featured_image.url
        ? `${it.featured_image.url}&width=168`
        : (it.image ? `${it.image}&width=168` : '');
      const title = escapeHtml(it.product_title || it.title || '');
      const variant = it.variant_title && it.variant_title !== 'Default Title'
        ? escapeHtml(it.variant_title)
        : '';

      // Discount detection from line_level_discount_allocations
      const original = it.original_line_price ?? (it.original_price * it.quantity);
      const final = it.final_line_price ?? it.line_price ?? (it.price * it.quantity);
      const hasDiscount = original > final;
      const isFree = final === 0 && hasDiscount;

      const priceBlock = isFree
        ? `<div class="cd-item__price-strike">${fmtMoney(it.original_price)}</div>
           <div class="cd-item__price-free">FREE</div>`
        : hasDiscount
          ? `<div class="cd-item__price-strike">${fmtMoney(it.original_price)}</div>
             <div class="cd-item__price">${fmtMoney(it.final_price ?? it.price)}</div>`
          : `<div class="cd-item__price">${fmtMoney(it.final_price ?? it.price)}</div>`;

      const decLabel = it.quantity <= 1 ? 'Remove item' : 'Decrease quantity';

      return `
        <div class="cd-item" data-cd-key="${key}">
          <div class="cd-item__thumb">
            ${img ? `<img src="${escapeHtml(img)}" alt="${title}" loading="lazy" width="168" height="168">` : ''}
          </div>
          <div class="cd-item__meta">
            <div class="cd-item__row1">
              <div style="min-width:0;">
                <span class="cd-item__title">${title}</span>
                ${variant ? `<div class="cd-item__variant">${variant}</div>` : ''}
              </div>
              <div class="cd-item__price-wrap">${priceBlock}</div>
              <div class="cd-v2 cd-item__line">${hasDiscount ? `<s>${fmtMoney(original)}</s>` : ''}${isFree ? 'Free' : fmtMoney(final)}</div>
            </div>
            <div class="cd-item__row2">
              <div class="cd-qty" role="group" aria-label="Quantity">
                <button type="button" data-cd-action="dec" data-cd-key="${key}" aria-label="${decLabel}">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/></svg>
                </button>
                <div class="cd-qty__value" aria-live="polite">${it.quantity}</div>
                <button type="button" data-cd-action="inc" data-cd-key="${key}" aria-label="Increase quantity">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 5v14"/><path d="M5 12h14"/>
                  </svg>
                </button>
              </div>
              <button type="button" class="cd-item__remove" data-cd-action="remove" data-cd-key="${key}" aria-label="Remove ${title}">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  <path d="M6 6v14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                </svg>
                <span>Remove</span>
              </button>
            </div>
          </div>
        </div>
      `;
    }
  }

  customElements.define('tms-cart-drawer', CartDrawer);
})();



